import React from "react";

import { Category, DocumentationView, NotificationItem, TutorialItem } from "../types";
import NotificationEditView from "./views/NotificationEditView";
import NotificationPostView from "./views/NotificationPostView";
import TutorialEditView from "./views/TutorialEditView";
import TutorialTileView from "./views/TutorialTileView";
import TutorialVideoView from "./views/TutorialVideoView";

interface DocumentationViewRendererProps {
  activeView: DocumentationView;
  selectedItemId?: string;
  selectedCategory?: Category;
  searchTerm?: string;
  tutorials?: TutorialItem[];
  notifications?: NotificationItem[];
  selectedNotificationCache?: NotificationItem | null;
  canEdit: boolean;
  onViewChange: (view: DocumentationView) => void;
  onSelectItem: (itemId: string) => void;
  onSaveNotification?: (notification: NotificationItem) => void;
  onDeleteNotification?: (notificationId: number | string) => void;
}

const DocumentationViewRenderer: React.FC<DocumentationViewRendererProps> = ({
  activeView,
  selectedItemId,
  selectedCategory,
  searchTerm = "",
  tutorials = [],
  notifications = [],
  selectedNotificationCache,
  canEdit,
  onViewChange,
  onSelectItem,
  onSaveNotification,
  onDeleteNotification
}) => {
  const handleSelectVideo = (videoId: string) => {
    onSelectItem(videoId);
    onViewChange(DocumentationView.TutorialVideoView);
  };

  const handleBackToTiles = () => {
    onViewChange(DocumentationView.TutorialTileView);
  };

  const handleEditTutorial = () => {
    onViewChange(DocumentationView.TutorialEditView);
  };

  const handleEditNotification = () => {
    onViewChange(DocumentationView.NotificationEditView);
  };

  const handleSaveNotificationAndReturn = (notification: NotificationItem) => {
    if (onSaveNotification) {
      onSaveNotification(notification);
    }
    // Return to post view if editing existing, notification list view if creating new
    onViewChange(DocumentationView.NotificationPostView);
  };

  // Find the specific tutorial or notification based on selectedItemId
  const selectedTutorial =
    selectedItemId && Array.isArray(tutorials)
      ? tutorials.find((t) => t.id === selectedItemId)
      : undefined;
  const selectedNotification = Array.isArray(notifications)
    ? notifications.find((n) => n.id.toString() === selectedItemId) ||
      (selectedNotificationCache?.id.toString() === selectedItemId
        ? selectedNotificationCache
        : null)
    : null;

  switch (activeView) {
    case DocumentationView.TutorialTileView:
      return (
        <TutorialTileView
          tutorials={tutorials}
          selectedCategory={selectedCategory}
          searchTerm={searchTerm}
          onSelectVideo={handleSelectVideo}
          onEdit={
            canEdit
              ? (tutorialId: string) => {
                  onSelectItem(tutorialId);
                  handleEditTutorial();
                }
              : undefined
          }
          canEdit={canEdit}
        />
      );

    case DocumentationView.TutorialVideoView:
      return (
        <TutorialVideoView
          tutorial={selectedTutorial}
          videoId={selectedItemId || ""}
          onBack={handleBackToTiles}
          onEdit={canEdit ? handleEditTutorial : undefined}
          canEdit={canEdit}
        />
      );

    case DocumentationView.TutorialEditView:
      return (
        <TutorialEditView
          key={selectedItemId || "new-tutorial"} // Force remount when switching between edit and create
          tutorial={selectedTutorial}
          onCancel={() => {
            // Return to video view if editing existing, tile view if creating new
            const returnView = selectedTutorial
              ? DocumentationView.TutorialVideoView
              : DocumentationView.TutorialTileView;
            onViewChange(returnView);
          }}
          onSuccess={(wasDeleted = false) => {
            // Always go to tile view if deleted, otherwise follow normal flow
            if (wasDeleted) {
              onViewChange(DocumentationView.TutorialTileView);
            } else {
              // Navigate after successful create/update
              const returnView = selectedItemId
                ? DocumentationView.TutorialVideoView
                : DocumentationView.TutorialTileView;
              onViewChange(returnView);
            }
          }}
          isNew={!selectedTutorial}
        />
      );

    case DocumentationView.NotificationPostView:
      return (
        <NotificationPostView
          notification={selectedNotification}
          postId={selectedItemId || ""}
          onEdit={canEdit ? handleEditNotification : undefined}
          canEdit={canEdit}
        />
      );

    case DocumentationView.NotificationEditView:
      return (
        <NotificationEditView
          notification={selectedNotification}
          postId={selectedItemId}
          onCancel={() => {
            onViewChange(DocumentationView.NotificationPostView);
          }}
          onSave={handleSaveNotificationAndReturn}
          onDelete={onDeleteNotification}
          isNew={!selectedNotification}
        />
      );

    default:
      return <div>Unknown view: {activeView}</div>;
  }
};

export default DocumentationViewRenderer;
